/**
 * Zustand store for the planet management panel.
 *
 * Holds:
 *   - panel visibility / target planet
 *   - server snapshot (profile, structures, richness diffs, activity log, credits)
 *   - selected zone (lat/lon clicked on globe)
 *   - last scan detail returned by the server
 *
 * Mutations from server broadcasts (structure_planted, structure_dismantled,
 * structure_depleted, buffer_updated, activity_log_entry) are applied here.
 */

import { create } from 'zustand';

// ==================== TYPES ====================

export interface PlanetProfileWire {
  profileSeed: number;
  sizeMultiplier: number;
  richnessMultiplier: number;
  tierBias: { T1: number; T2: number; T3: number; T4: number };
  categoryBias: { organic: number; crystal: number };
  clusterDensity: number;
}

export interface PlanetWire {
  id: string;
  name: string;
  planetType: string;
  constellationTier: number;
  profile: PlanetProfileWire;
  radiusKm: number;
  /** Hexsphere mesh frequency: 6 = G(6)/362 cells, 8 = G(8)/642, 10 = G(10)/1002. */
  meshFrequency: 6 | 8 | 10;
}

export interface MiningStatsWire {
  organic: { resourceId: string; tier: number; richness: number; rate: number };
  crystal: { resourceId: string; tier: number; richness: number; rate: number };
  totalMined: Record<string, number>;
  parentOutpostBuffer: Record<string, number>;
}

export interface StructureWire {
  id: string;
  planet_id: string;
  owner_player_id: string;
  structure_type: 'outpost' | 'mining_station' | 'relay' | 'scanner';
  lat: number;
  lon: number;
  /** Authoritative hex (Phase 4+). May be null for legacy rows pre-backfill. */
  hex_id: number | null;
  radius_km: number;
  primary_resource_type: string | null;
  total_mined: Record<string, number>;
  buffer_contents: Record<string, number>;
  buffer_max: number;
  parent_outpost_id: string | null;
  is_active: boolean;
  planted_at: string;
  last_upkeep_at: string;
  grace_period_started_at: string | null;
}

export interface RichnessDiffWire {
  cell_lat: number;
  cell_lon: number;
  depletion_organic: number;
  depletion_crystal: number;
}

/**
 * Per-hex delta state. Replaces RichnessDiffWire starting Phase 3.
 * delta is signed: negative = mined, positive = enriched by migration (Phase 5).
 * Effective richness = max(0, baseline + delta).
 */
export interface HexStateWire {
  hex_id: number;
  delta_organic: number;
  delta_crystal: number;
}

export interface ActivityEntryWire {
  id?: number;
  player_id: string | null;
  event_type:
    | 'plant'
    | 'dismantle'
    | 'depleted'
    | 'auto_dismantle'
    | 'claim'
    | 'reactivated'
    | 'geological_shift';
  structure_type?: string | null;
  structure_id?: string | null;
  message: string;
  created_at: string;
}

export interface ScanDetailsWire {
  lat: number;
  lon: number;
  cell: { cellLat: number; cellLon: number };
  organic: { richness: number; resourceId: string; tier: number };
  crystal: { richness: number; resourceId: string; tier: number };
  resolution: 'orbital' | 'scanner';
}

export interface SelectedZone {
  lat: number;
  lon: number;
  /** id of the clicked structure if any (else null) */
  clickedStructureId: string | null;
}

export interface GlobeLayers {
  /** Terrain is always rendered unless resources is active. */
  terrain: boolean;
  /** When true, shows resource overlay and hides terrain. */
  noise: boolean;
}

export interface PlanetPanelState {
  isOpen: boolean;
  /** Planet currently being viewed (or null when panel closed). */
  planetId: string | null;
  /** Local player's id (so we can colour-code own vs other structures). */
  selfPlayerId: string | null;

  planet: PlanetWire | null;
  structures: StructureWire[];
  /** Per-mining-station live stats keyed by structure id. */
  miningStats: Record<string, MiningStatsWire>;
  /** Per-km-cell depletion (deprecated, populated empty from Phase 3+). */
  richnessDiffs: RichnessDiffWire[];
  /** Per-hex signed delta. Authoritative from Phase 3+. */
  hexState: HexStateWire[];
  activity: ActivityEntryWire[];
  credits: number;

  /** Currently selected point on the globe (right panel context). */
  selection: SelectedZone | null;

  /** Last server-returned scan detail for the selected point. */
  scanDetails: ScanDetailsWire | null;

  /** Loading flag — open is pending until snapshot arrives. */
  isLoading: boolean;
  /** Last error message (cleared on next action). */
  lastError: string | null;

  /** Low-res orbital scan grid (cells indexed by lat, lon centres). */
  orbitalScan: Array<{ lat: number; lon: number; organic: number; crystal: number }>;
  orbitalScanGrid: { latCells: number; lonCells: number };

  /** Bumped whenever richnessDiffs change — triggers resource texture re-bake. */
  resourceVersion: number;

  /** Globe texture layer toggles. */
  layers: GlobeLayers;

  /** When non-null, the globe should animate to centre on this lat/lon. */
  centerTarget: { lat: number; lon: number; seq: number } | null;

  /** True while the globe is baking a texture (LOD change or resource toggle). */
  isBakingTexture: boolean;

  // ==================== MUTATIONS ====================
  setSelfPlayerId(id: string | null): void;
  beginOpen(planetId: string): void;
  applySnapshot(snapshot: {
    planet: PlanetWire;
    structures: StructureWire[];
    miningStats: Record<string, MiningStatsWire>;
    richnessDiffs: RichnessDiffWire[];
    hexState: HexStateWire[];
    activityLog: ActivityEntryWire[];
    playerCredits: number;
    orbitalScan: Array<{ lat: number; lon: number; organic: number; crystal: number }>;
    orbitalScanGrid: { latCells: number; lonCells: number };
  }): void;
  finishClose(): void;
  setError(message: string | null): void;
  setSelection(selection: SelectedZone | null): void;
  setScanDetails(details: ScanDetailsWire | null): void;
  setCredits(credits: number): void;

  toggleLayer(layer: keyof GlobeLayers): void;
  setIsBakingTexture(v: boolean): void;
  requestCenter(lat: number, lon: number): void;

  // Broadcast appliers
  upsertStructure(structure: StructureWire): void;
  removeStructure(structureId: string): void;
  markStructureDepleted(structureId: string): void;
  setStructureActive(structureId: string, isActive: boolean): void;
  updateBuffer(outpostId: string, contents: Record<string, number>): void;
  prependActivity(entry: ActivityEntryWire): void;
  mergeRichnessDiffs(diffs: RichnessDiffWire[]): void;
  mergeHexState(rows: HexStateWire[]): void;
}

export const usePlanetPanelStore = create<PlanetPanelState>((set, get) => ({
  isOpen: false,
  planetId: null,
  selfPlayerId: null,
  planet: null,
  structures: [],
  miningStats: {},
  richnessDiffs: [],
  hexState: [],
  activity: [],
  credits: 0,
  selection: null,
  scanDetails: null,
  isLoading: false,
  lastError: null,
  orbitalScan: [],
  orbitalScanGrid: { latCells: 18, lonCells: 36 },
  resourceVersion: 0,
  layers: { terrain: true, noise: false },
  isBakingTexture: false,
  centerTarget: null,

  setSelfPlayerId(id) {
    set({ selfPlayerId: id });
  },

  beginOpen(planetId) {
    set({
      isOpen: true,
      isLoading: true,
      planetId,
      planet: null,
      structures: [],
      miningStats: {},
      richnessDiffs: [],
      hexState: [],
      activity: [],
      credits: 0,
      selection: null,
      scanDetails: null,
      lastError: null,
      orbitalScan: [],
    });
  },

  applySnapshot(snapshot) {
    set({
      isLoading: false,
      planet: snapshot.planet,
      structures: snapshot.structures,
      miningStats: snapshot.miningStats ?? {},
      richnessDiffs: snapshot.richnessDiffs ?? [],
      hexState: snapshot.hexState ?? [],
      activity: snapshot.activityLog,
      credits: snapshot.playerCredits,
      orbitalScan: snapshot.orbitalScan,
      orbitalScanGrid: snapshot.orbitalScanGrid,
      resourceVersion: get().resourceVersion + 1,
    });
  },

  finishClose() {
    set({
      isOpen: false,
      isLoading: false,
      planetId: null,
      planet: null,
      structures: [],
      miningStats: {},
      richnessDiffs: [],
      hexState: [],
      activity: [],
      credits: 0,
      selection: null,
      scanDetails: null,
      lastError: null,
      orbitalScan: [],
      centerTarget: null,
    });
  },

  setError(message) {
    set({ lastError: message, isLoading: false });
  },

  setSelection(selection) {
    set({ selection, scanDetails: null });
  },

  setScanDetails(details) {
    set({ scanDetails: details });
  },

  setCredits(credits) {
    set({ credits });
  },

  toggleLayer(layer) {
    if (layer === 'noise') {
      const wasOn = get().layers.noise;
      // Resources ON → terrain OFF. Resources OFF → terrain ON.
      set({ layers: { terrain: wasOn, noise: !wasOn } });
    } else {
      // Shouldn't be called directly, but safe fallback.
      set({ layers: { terrain: true, noise: false } });
    }
  },

  setIsBakingTexture(v) {
    set({ isBakingTexture: v });
  },

  requestCenter(lat, lon) {
    const prev = get().centerTarget;
    set({ centerTarget: { lat, lon, seq: (prev?.seq ?? 0) + 1 } });
  },

  upsertStructure(structure) {
    const existing = get().structures;
    const idx = existing.findIndex((s) => s.id === structure.id);
    if (idx >= 0) {
      const next = existing.slice();
      next[idx] = structure;
      set({ structures: next });
    } else {
      set({ structures: [...existing, structure] });
    }
  },

  removeStructure(structureId) {
    const sel = get().selection;
    set({
      structures: get().structures.filter((s) => s.id !== structureId),
      // Clear selection if the dismantled structure was selected.
      ...(sel?.clickedStructureId === structureId ? { selection: null, scanDetails: null } : {}),
    });
  },

  markStructureDepleted(structureId) {
    set({
      structures: get().structures.map((s) =>
        s.id === structureId ? { ...s, is_active: false } : s
      ),
    });
  },

  setStructureActive(structureId, isActive) {
    set({
      structures: get().structures.map((s) =>
        s.id === structureId ? { ...s, is_active: isActive } : s
      ),
    });
  },

  updateBuffer(outpostId, contents) {
    set({
      structures: get().structures.map((s) =>
        s.id === outpostId ? { ...s, buffer_contents: contents } : s
      ),
    });
  },

  prependActivity(entry) {
    set({
      activity: [entry, ...get().activity].slice(0, 50),
    });
  },

  mergeRichnessDiffs(diffs) {
    const existing = get().richnessDiffs;
    const map = new Map<string, RichnessDiffWire>();
    for (const d of existing) map.set(`${d.cell_lat},${d.cell_lon}`, d);
    for (const d of diffs) map.set(`${d.cell_lat},${d.cell_lon}`, d);
    set({
      richnessDiffs: Array.from(map.values()),
      resourceVersion: get().resourceVersion + 1,
    });
  },

  mergeHexState(rows) {
    const map = new Map<number, HexStateWire>();
    for (const r of get().hexState) map.set(r.hex_id, r);
    for (const r of rows) map.set(r.hex_id, r);
    set({
      hexState: Array.from(map.values()),
      resourceVersion: get().resourceVersion + 1,
    });
  },
}));
